/**
 * @module utils
 * @private
 */
import { version } from "../../package.json";
import defaults from "../constants";
import helpers from "./helpers";
import { cache } from "../web/persistence/cache";
import { memoryCache } from "../web/persistence/memoryCache";
import { network } from "web-utils-network";
import initPrivateData from "./private";

/**
 * Generic builder for SDK objects.
 */
export default {
  SDK(api) {
    return ({
      lang = defaults.lang,
      networkRequestTimeouts = defaults.networkRequestTimeouts,
      environment = defaults.environment,
      backendHosts = defaults.backendHosts,
      localCache = defaults.localCache,
      supportedLanguages = defaults.supportedLanguages,
      platformName = defaults.platformName,
      drmTechnology = defaults.drmTechnology,
      drmLicenseServer = defaults.drmLicenseServer,
      heartbeatPeriod = defaults.heartbeatPeriod,
      filmstripsTimeJump = defaults.filmstripsTimeJump,
      layoutCacheTime = defaults.layoutCacheTime,
      channelEventsCacheTime = defaults.channelEventsCacheTime,
      categoryEventsCacheTime = defaults.categoryEventsCacheTime,
      startEpgEventsCacheTime = defaults.startEpgEventsCacheTime,
      epgConfigsCacheTime = defaults.epgConfigsCacheTime,
      allMopsCacheTime = defaults.allMopsCacheTime,
      allSubscriptionDeepQueryCacheTime = defaults.allSubscriptionDeepQueryCacheTime,
      deepAddOnsCacheTime = defaults.deepAddOnsCacheTime,
      paymentMethodCacheTime = defaults.paymentMethodCacheTime,
      subscriptionsCacheTime = defaults.subscriptionsCacheTime,
      titlesCacheTime = defaults.titlesCacheTime,
      seasonsCacheTime = defaults.seasonsCacheTime,
      episodesCacheTime = defaults.episodesCacheTime,
      genresCacheTime = defaults.genresCacheTime,
      devicesCacheTime = defaults.devicesCacheTime,
      feedsCacheTime = defaults.feedsCacheTime,
      userCacheTime = defaults.userCacheTime,
      userPreferencesCacheTime = defaults.userPreferencesCacheTime,
      memoryCacheTime = defaults.memoryCacheTime,
      memoryCacheLimit = defaults.memoryCacheLimit,
      minimumCacheCleanupTime = defaults.minimumCacheCleanupTime,
      tokenExpirationTime = defaults.tokenExpirationTime,
      currentProfileExpirationTime = defaults.currentProfileExpirationTime,
      phoneVerificationDataExpirationTime = defaults.phoneVerificationDataExpirationTime,
      maxUserDevices = defaults.maxUserDevices,
      imageWidths = defaults.imageWidths,
      securitylevel4K = defaults.securitylevel4K,
      appVersion = defaults.appVersion,
      publicVersion = defaults.publicVersion,
      parentalControlLevels = defaults.parentalControlLevels,
      playbackAnalytics = defaults.playbackAnalytics,
      appName = defaults.appName,
      clientType = defaults.clientType,
      analyticsId = defaults.analyticsId,
      maxBitrate = defaults.maxBitrate,
      searchConfig = defaults.searchConfig,
      identityProviders = defaults.identityProviders,
      shuffleLayoutStaticModules = defaults.shuffleLayoutStaticModules,
      jwtCookie = defaults.jwtCookie,
      guestJwtCookie = defaults.guestJwtCookie,
      paymentMethodDictionaryCacheTime = defaults.paymentMethodDictionaryCacheTime,
      multipleProfiles = defaults.multipleProfiles,
      avatarsListCacheTime = defaults.avatarsListCacheTime
    } = {}) => {
      const privateKey = {};
      const getPrivateData = self => self._getPrivateData(privateKey);
      const sdk = Object.create(
        {},
        {
          lang: {
            value: lang,
            writable: true
          },
          networkRequestTimeouts: {
            value: networkRequestTimeouts,
            writable: true
          },
          environment: {
            value: environment,
            writable: true
          },
          http: {
            value: network.create({ timeout: networkRequestTimeouts.default, headers: { "Client-Type": clientType } }),
            writable: true
          },
          backendHosts: {
            value: backendHosts,
            writable: false
          },
          supportedLanguages: {
            value: supportedLanguages,
            writable: false
          },
          localCache: {
            value: localCache,
            writable: false
          },
          platformName: {
            value: platformName,
            writable: false
          },
          drmTechnology: {
            value: drmTechnology,
            writable: false
          },
          drmLicenseServer: {
            value: drmLicenseServer,
            writable: false
          },
          heartbeatPeriod: {
            value: heartbeatPeriod,
            writable: false
          },
          filmstripsTimeJump: {
            value: filmstripsTimeJump,
            writable: false
          },
          layoutCacheTime: {
            value: layoutCacheTime,
            writable: false
          },
          channelEventsCacheTime: {
            value: channelEventsCacheTime,
            writable: false
          }, // old EPG event

          categoryEventsCacheTime: {
            value: categoryEventsCacheTime,
            writable: false
          },

          epgConfigsCacheTime: {
            value: epgConfigsCacheTime,
            writable: false
          },
          startEpgEventsCacheTime: {
            value: startEpgEventsCacheTime,
            writable: false
          },
          allMopsCacheTime: {
            value: allMopsCacheTime,
            writable: false
          },
          allSubscriptionDeepQueryCacheTime: {
            value: allSubscriptionDeepQueryCacheTime,
            writable: false
          },
          deepAddOnsCacheTime: {
            value: deepAddOnsCacheTime,
            writable: false
          },
          paymentMethodDictionaryCacheTime: {
            value: paymentMethodDictionaryCacheTime,
            writable: false
          },
          paymentMethodCacheTime: {
            value: paymentMethodCacheTime,
            writable: false
          },
          subscriptionsCacheTime: {
            value: subscriptionsCacheTime,
            writable: false
          },
          titlesCacheTime: {
            value: titlesCacheTime,
            writable: false
          },
          seasonsCacheTime: {
            value: seasonsCacheTime,
            writable: false
          },
          episodesCacheTime: {
            value: episodesCacheTime,
            writable: false
          },
          genresCacheTime: {
            value: genresCacheTime,
            writable: false
          },
          feedsCacheTime: {
            value: feedsCacheTime,
            writable: false
          },
          devicesCacheTime: {
            value: devicesCacheTime,
            writable: false
          },
          userCacheTime: {
            value: userCacheTime,
            writable: false
          },
          userPreferencesCacheTime: {
            value: userPreferencesCacheTime,
            writable: false
          },
          memoryCacheTime: {
            value: memoryCacheTime,
            writable: false
          },
          memoryCacheLimit: {
            value: memoryCacheLimit,
            writable: false
          },
          minimumCacheCleanupTime: {
            value: minimumCacheCleanupTime,
            writable: false
          },
          tokenExpirationTime: {
            value: tokenExpirationTime,
            writable: false
          },
          currentProfileExpirationTime: {
            value: currentProfileExpirationTime,
            writable: false
          },
          phoneVerificationDataExpirationTime: {
            value: phoneVerificationDataExpirationTime,
            writable: false
          },
          maxUserDevices: {
            value: maxUserDevices,
            writable: true
          },
          imageWidths: {
            value: imageWidths,
            writable: false
          },
          securitylevel4K: {
            value: securitylevel4K,
            writable: false
          },
          appVersion: {
            value: appVersion,
            writable: false
          },
          publicVersion: {
            value: publicVersion,
            writable: false
          },
          parentalControlLevels: {
            value: parentalControlLevels,
            writable: false
          },
          playbackAnalytics: {
            value: playbackAnalytics,
            writable: false
          },
          appName: {
            value: appName,
            writable: false
          },
          clientType: {
            value: clientType,
            writable: false
          },
          analyticsId: {
            value: analyticsId,
            writable: false
          },
          maxBitrate: {
            value: maxBitrate,
            writable: false
          },
          searchConfig: {
            value: searchConfig,
            writable: false
          },
          identityProviders: {
            value: identityProviders,
            writable: true
          },
          requestTimeout: {
            value: defaults.requestTimeout,
            writable: false
          },
          shuffleLayoutStaticModules: {
            value: shuffleLayoutStaticModules,
            writable: false
          },
          jwtCookie: {
            value: jwtCookie,
            writable: false
          },
          guestJwtCookie: {
            value: guestJwtCookie,
            writable: false
          },
          multipleProfiles: {
            value: multipleProfiles,
            writable: false
          },
          avatarsListCacheTime: {
            value: avatarsListCacheTime,
            writable: false
          },

          // Private accessors.
          version: {
            get() {
              return getPrivateData(this).version;
            }
          },
          parentalLevel: {
            get() {
              return getPrivateData(this).parentalLevel;
            }
          },
          __updatePCL: {
            value(parentalLevel, self) {
              getPrivateData(self).parentalLevel = parentalLevel;
            }
          }
        }
      );

      // Init private attributes.
      initPrivateData(sdk, privateKey, {
        version,
        parentalLevel: defaults.parentalLevel
      });

      // Add common helpers.
      Object.assign(sdk, helpers);

      if (localCache !== false) {
        cache.init(sdk.minimumCacheCleanupTime);
        cache.setSDKInstance(sdk);
        if (localCache.memoryCache !== false) {
          memoryCache.init(sdk.memoryCacheTime, sdk.memoryCacheLimit);
        }
      }

      // Return environment specialized SDK.
      return Object.assign(sdk, api(sdk));
    };
  }
};
